import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Gauge, Calendar, Weight, User, Send, ShieldCheck, Lock, HeartHandshake, Clock, CheckCircle2, Languages, Truck, Wallet, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { mediaUrl, formatApiError, listingPriceParts, localizedName } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import ResourceWidgets from "@/components/ResourceWidgets";
import { TrustChips } from "@/components/ListingMatrix";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { dynDisplayValue } from "@/lib/listingDyn";
import { LANGUAGES, DELIVERY_CHANNELS, findOptLabel, coverageSummary, pricingSummary } from "@/lib/serviceFields";
import { toast } from "sonner";

const PLACEHOLDER = "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?crop=entropy&cs=srgb&fm=jpg&q=80&w=940";
const REQUEST_TYPES = ["investment", "apprenticeship", "donation"];
const REQ_KEY = { investment: "Investment", apprenticeship: "Apprenticeship", donation: "Donation" };

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const lng = (i18n.language || "ro").slice(0, 2);
  const [l, setL] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [reqType, setReqType] = useState("investment");
  const [reqMessage, setReqMessage] = useState("");
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(() => api.get(`/listings/${id}`).then((r) => setL(r.data)).catch(() => setNotFound(true)), [id]);
  useEffect(() => { load(); }, [load]);

  const requestAccess = async () => {
    if (!user) return navigate("/autentificare");
    setRequesting(true);
    try {
      await api.post("/investment-requests", { listing_id: id, request_type: reqType, message: reqMessage });
      toast.success("Cerere de acces trimisă! Vei fi notificat când proprietarul răspunde.");
      setReqMessage("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRequesting(false);
    }
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim())
      return toast.error("Completează nume, email și mesaj");
    setSending(true);
    try {
      await api.post(`/listings/${id}/contact`, form);
      toast.success("Mesajul a fost trimis vânzătorului!");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-24 text-center"><p className="text-slate-500">Anunțul nu a fost găsit.</p><Link to="/anunturi" className="text-orange-600 font-semibold hover:underline mt-4 inline-block">← Toate anunțurile</Link></div>;
  if (!l) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-400">Se încarcă...</div>;

  const imgs = l.images && l.images.length ? l.images : [l.image];
  const s = l.specs || {};
  const specRows = [
    s.motor_power_kw != null && { icon: Gauge, label: "Putere motor", val: `${s.motor_power_kw} kW` },
    s.weight_kg != null && { icon: Weight, label: "Greutate", val: `${s.weight_kg} kg` },
    s.max_rpm != null && { icon: Gauge, label: "Turație max", val: `${s.max_rpm} rpm` },
    s.spindle_diameter_mm != null && { icon: Gauge, label: "Diametru arbore", val: `${s.spindle_diameter_mm} mm` },
    s.year != null && { icon: Calendar, label: "An fabricație", val: s.year },
  ].filter(Boolean);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4" data-testid="listing-detail-page">
      <Link to="/anunturi" className="text-sm text-slate-500 hover:text-orange-600">← Toate anunțurile</Link>
      {/* Trust ribbon — elevated to the very top for the high-value trader */}
      <div className="flex items-center gap-2 mt-2 mb-1 min-h-[24px]" data-testid="trust-ribbon"><TrustChips l={l} size="lg" /></div>
      <div className="grid lg:grid-cols-2 gap-6 mt-2">
        <div>
          <div className="relative aspect-[4/3] bg-slate-100 border border-slate-200 overflow-hidden" data-testid="gallery-main">
            <img src={mediaUrl(imgs[active]) || PLACEHOLDER} alt={l.name} onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} className="w-full h-full object-cover" />
            {imgs.length > 1 && (
              <>
                <button onClick={() => setActive((a) => (a - 1 + imgs.length) % imgs.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-slate-900/70 hover:bg-slate-900 text-white flex items-center justify-center transition-colors" data-testid="gallery-prev" aria-label="Prev"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setActive((a) => (a + 1) % imgs.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-slate-900/70 hover:bg-slate-900 text-white flex items-center justify-center transition-colors" data-testid="gallery-next" aria-label="Next"><ChevronRight className="w-4 h-4" /></button>
                <span className="absolute bottom-2 right-2 bg-slate-900/80 text-white text-[11px] font-bold font-mono px-2 py-0.5" data-testid="gallery-counter">{active + 1}/{imgs.length}</span>
              </>
            )}
          </div>
          {imgs.length > 1 && (
            <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1" data-testid="gallery-thumbs">
              {imgs.map((im, i) => (
                <button key={im} onClick={() => setActive(i)} className={`w-16 h-16 shrink-0 border-2 overflow-hidden transition-colors ${i === active ? "border-orange-600" : "border-slate-200 hover:border-slate-400"}`} data-testid={`thumb-${i}`}>
                  <img src={mediaUrl(im) || PLACEHOLDER} alt="" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <Badge className="rounded-none bg-orange-600 mb-3">Anunț utilizator</Badge>
          {l.ai_verified_price && (
            <span className="inline-flex items-center gap-1.5 bg-amber-500 text-slate-900 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 ml-2 mb-3 align-middle" data-testid="ai-verified-badge-detail">
              <ShieldCheck className="w-3.5 h-3.5" /> AI Verified Price
            </span>
          )}
          {l.is_social_impact && (
            <span className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 ml-2 mb-3 align-middle" data-testid="social-impact-badge">
              <HeartHandshake className="w-3.5 h-3.5" /> {t("trades.badge")}
            </span>
          )}
          <h1 className="text-3xl font-black tracking-tight text-slate-900">{localizedName(l, i18n.language)}</h1>
          {l.is_social_impact && l.association_name && (
            <div className="text-sm text-emerald-700 font-semibold mt-1" data-testid="association-name">{l.association_name}</div>
          )}
          {l.location && <div className="flex items-center gap-2 text-slate-500 mt-2"><MapPin className="w-4 h-4" /> {l.location}</div>}
          {!l.location && l.county && <div className="flex items-center gap-2 text-slate-500 mt-2"><MapPin className="w-4 h-4" /> {l.county} {l.masked && <span className="text-xs text-slate-400">{t("capital.confidentialLocation")}</span>}</div>}
          {(l.investment_min || l.investment_max) && (
            <div className="mt-4 inline-flex flex-col bg-indigo-50 border border-indigo-200 px-4 py-2" data-testid="investment-range">
              <span className="text-[11px] uppercase tracking-wide font-bold text-indigo-500">{t("capital.investmentRange")}</span>
              <span className="text-xl font-black text-indigo-900">
                {l.investment_min ? new Intl.NumberFormat("ro-RO").format(l.investment_min) : "—"} – {l.investment_max ? new Intl.NumberFormat("ro-RO").format(l.investment_max) : "—"} RON
              </span>
            </div>
          )}
          {!(l.investment_min || l.investment_max) && (() => { const p = listingPriceParts(l); return (
            <div className="mt-4" data-testid="listing-price">
              <div className="text-3xl font-black text-orange-600">{p.main}</div>
              {p.eur ? <div className="text-sm text-slate-400 font-mono mt-0.5">{p.eur}</div> : null}
            </div>
          ); })()}

          {l.main_parameters && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-1">Parametri principali</div>
              <p className="text-slate-700 whitespace-pre-line">{l.main_parameters}</p>
            </div>
          )}
          {l.description && <p className="text-slate-600 mt-4 whitespace-pre-line" data-testid="listing-description">{(l.description_i18n && l.description_i18n[lng]) || l.description}</p>}

          {l.service_details && (l.service_details.languages?.length || l.service_details.delivery_channels?.length || l.service_details.coverage || l.service_details.pricing?.pricing_type) && (
            <div className="mt-6 border border-indigo-200 bg-indigo-50/50 p-5 space-y-3" data-testid="service-details">
              <div className="text-xs uppercase tracking-wide font-bold text-indigo-800">{t("svc.title")}</div>
              {l.service_details.languages?.length > 0 && (
                <div className="flex items-start gap-2 text-sm">
                  <Languages className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
                  <div className="flex flex-wrap gap-1.5">
                    {l.service_details.languages.map((v) => (
                      <span key={v} className="bg-white border border-indigo-200 px-2 py-0.5 text-xs font-semibold text-slate-700" data-testid={`svc-lang-tag-${v}`}>{findOptLabel(LANGUAGES, v, lng)}</span>
                    ))}
                  </div>
                </div>
              )}
              {coverageSummary(l.service_details.coverage, t) && (
                <div className="flex items-center gap-2 text-sm text-slate-700"><MapPin className="w-4 h-4 text-indigo-600" /> {coverageSummary(l.service_details.coverage, t)}</div>
              )}
              {l.service_details.delivery_channels?.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-slate-700"><Truck className="w-4 h-4 text-indigo-600" /> {l.service_details.delivery_channels.map((v) => findOptLabel(DELIVERY_CHANNELS, v, lng)).join(" · ")}</div>
              )}
              {pricingSummary(l.service_details.pricing, lng, t) && (
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Wallet className="w-4 h-4 text-indigo-600" /> {pricingSummary(l.service_details.pricing, lng, t)}</div>
              )}
            </div>
          )}

          {specRows.length > 0 && (
            <div className="mt-6 border border-slate-200 divide-y divide-slate-100">
              {specRows.map((r) => (
                <div key={r.label} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="flex items-center gap-2 text-slate-500"><r.icon className="w-4 h-4" /> {r.label}</span>
                  <span className="font-semibold font-mono">{r.val}</span>
                </div>
              ))}
            </div>
          )}

          {l.dynamic_specs && Object.keys(l.dynamic_specs).length > 0 && (
            <div className="mt-6 border border-slate-200 divide-y divide-slate-100" data-testid="dynamic-specs">
              {Object.entries(l.dynamic_specs).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="text-slate-500">{t(`dyn.${k}`)}</span>
                  <span className="font-semibold">{dynDisplayValue(k, v, lng)}</span>
                </div>
              ))}
            </div>
          )}

          {(!l.is_anonymous || l.access_status === "granted") ? (
          <div className="mt-8 border border-slate-200 p-6 bg-slate-50" data-testid="contact-seller-form">
            <div className="flex items-center gap-2 font-bold text-slate-900 mb-1"><User className="w-5 h-5 text-orange-600" /> Contactează vânzătorul</div>
            <p className="text-sm text-slate-500 mb-4">
              Vânzător: {l.seller_name || "Utilizator OXO"}
              {l.seller_phone && <span className="block mt-1 text-slate-700 font-semibold">{t("capital.phone")}: {l.seller_phone}</span>}
              {l.company_name && <span className="block text-slate-700 font-semibold">{t("capital.company")}: {l.company_name}</span>}
            </p>
            {l.seller_phone && (
              <a
                href={`https://wa.me/${(l.seller_phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Bună ziua! Sunt interesat de anunțul „${l.title}" de pe OXO.ro.`)}`}
                target="_blank" rel="noopener noreferrer"
                className="mb-4 w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 font-bold uppercase tracking-wide text-sm transition-colors"
                data-testid="listing-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp Contact
              </a>
            )}
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder="Numele tău" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" data-testid="contact-name" />
                <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" data-testid="contact-email" />
              </div>
              <Input placeholder="Telefon (opțional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" data-testid="contact-phone" />
              <Textarea placeholder="Mesajul tău..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-none" data-testid="contact-message" />
              <button onClick={submit} disabled={sending} className="w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-6 py-3 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="send-contact">
                <Send className="w-4 h-4" /> {sending ? "Se trimite..." : "Trimite mesaj"}
              </button>
            </div>
          </div>
          ) : (
          <div className="mt-8 border border-indigo-200 p-6 bg-indigo-50/60" data-testid="access-request-panel">
            <div className="flex items-center gap-2 font-bold text-slate-900 mb-1"><Lock className="w-5 h-5 text-indigo-600" /> {t("capital.accessTitle")}</div>
            <p className="text-sm text-slate-500 mb-4">{t("capital.accessDesc")}</p>

            {l.access_status === "granted" && (
              <div className="flex items-center gap-2 text-green-700 font-semibold" data-testid="access-granted"><CheckCircle2 className="w-5 h-5" /> {t("capital.accessGranted")}</div>
            )}
            {l.access_status === "pending" && (
              <div className="flex items-center gap-2 text-amber-700 font-semibold" data-testid="access-pending"><Clock className="w-5 h-5" /> {t("capital.accessPending")}</div>
            )}
            {l.access_status === "rejected" && (
              <div className="text-red-600 font-semibold mb-3" data-testid="access-rejected">{t("capital.accessRejected")}</div>
            )}
            {l.access_status === "login_required" && (
              <button onClick={() => navigate("/autentificare")} className="w-full bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="access-login-cta">
                {t("capital.loginToRequest")}
              </button>
            )}
            {["none", "rejected"].includes(l.access_status) && user && (
              <div className="space-y-3">
                <div>
                  <div className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-1.5">{t("capital.requestType")}</div>
                  <div className="grid grid-cols-3 gap-2" data-testid="request-type-picker">
                    {REQUEST_TYPES.map((rt) => (
                      <button key={rt} type="button" onClick={() => setReqType(rt)}
                        className={`border px-2 py-2.5 text-left transition-colors ${reqType === rt ? "border-indigo-600 bg-white text-indigo-700" : "border-slate-300 text-slate-600 hover:border-slate-400"}`}
                        data-testid={`request-type-${rt}`}>
                        <div className="font-bold text-xs">{t(`capital.req${REQ_KEY[rt]}`)}</div>
                        <div className="text-[10px] opacity-70 leading-tight">{t(`capital.req${REQ_KEY[rt]}Sub`)}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <Textarea placeholder={t("capital.requestMsgPh")} value={reqMessage} onChange={(e) => setReqMessage(e.target.value)} className="rounded-none bg-white" data-testid="request-message" />
                <button onClick={requestAccess} disabled={requesting} className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white px-6 py-3 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="submit-access-request">
                  <Send className="w-4 h-4" /> {requesting ? t("capital.sending") : t("capital.submitRequest")}
                </button>
              </div>
            )}
          </div>
          )}

          <div className="mt-4">
            <Accordion type="single" collapsible className="border border-slate-200" data-testid="listing-resources-accordion">
              <AccordionItem value="resources" className="border-none">
                <AccordionTrigger className="px-4 py-3 text-sm font-bold hover:no-underline" data-testid="listing-resources-trigger">{t("ux.more")}</AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <ResourceWidgets query={l.name} />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </div>
  );
}
